// File: app/src/hooks/useWorkoutProfile.js
// User profile and LLM settings state, plus auth sync effects.

import { useCallback, useEffect, useRef } from "react";
import useLocalStorage from "./useLocalStorage";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import {
  DEFAULT_LLM_SETTINGS,
  llmSettingsToApi,
  mergeFetchedLlmSettings,
  shouldSyncLlmSettingsToServer,
} from "../utils/llmSettings";
import { DEFAULT_USER_PROFILE } from "../utils/workoutPlanUtils";
import {
  mergeLocalProfileWithCloud,
  profilesAreEquivalent,
  profileNeedsServerSync,
  toApiProfile,
} from "../utils/profileSync";

const useWorkoutProfile = () => {
  const [userProfile, setUserProfile] = useLocalStorage(
    "userProfile",
    DEFAULT_USER_PROFILE,
  );

  // NEW: State for LLM Settings (with per-provider key storage)
  const [llmSettingsLocal, setLlmSettingsLocal] = useLocalStorage(
    "llmSettings",
    DEFAULT_LLM_SETTINGS,
  );
  const llmSettingsLocalRef = useRef(llmSettingsLocal);
  llmSettingsLocalRef.current = llmSettingsLocal;

  // Auth integration - sync profile when user logs in
  const { isAuthenticated } = useAuth();

  const setLlmSettings = useCallback(
    (newSettings) => {
      // Update per-provider key map
      const apiKeys = {
        ...(newSettings.apiKeys || llmSettingsLocal.apiKeys || {}),
      };
      if (newSettings.apiKey && newSettings.provider !== "ollama") {
        apiKeys[newSettings.provider] = newSettings.apiKey;
      }
      const updated = {
        ...DEFAULT_LLM_SETTINGS,
        ...newSettings,
        apiKeys,
        apiKey:
          newSettings.provider === "ollama"
            ? ""
            : newSettings.apiKey || apiKeys[newSettings.provider] || "",
      };
      setLlmSettingsLocal(updated);

      if (isAuthenticated) {
        api.updateSettings(llmSettingsToApi(updated)).catch(console.error);
      }
    },
    [isAuthenticated, setLlmSettingsLocal, llmSettingsLocal.apiKeys],
  );

  // Sync settings when authenticated
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let isActive = true;

    api
      .getSettings()
      .then((dbSettings) => {
        if (!isActive) return;

        const localSettings = llmSettingsLocalRef.current;
        const fetchedSettings = dbSettings || {};
        const mergedSettings = mergeFetchedLlmSettings(
          localSettings,
          fetchedSettings,
        );

        setLlmSettingsLocal(mergedSettings);

        if (shouldSyncLlmSettingsToServer(localSettings, fetchedSettings)) {
          api
            .updateSettings(llmSettingsToApi(mergedSettings))
            .catch(console.error);
        }
      })
      .catch(console.error);

    return () => {
      isActive = false;
    };
  }, [isAuthenticated, setLlmSettingsLocal]);

  // Expose llmSettings as simple value (from local state)
  const llmSettings = llmSettingsLocal;

  const {
    profile: cloudProfile,
    hasLoadedUserData,
    syncProfile,
    updateProfile: updateCloudProfile,
  } = useUserProfile();

  // When user logs in, sync local profile to cloud
  useEffect(() => {
    if (!isAuthenticated || !hasLoadedUserData) return;

    if (cloudProfile) {
      const mergedProfile = mergeLocalProfileWithCloud(
        userProfile,
        cloudProfile,
      );

      if (!profilesAreEquivalent(userProfile, mergedProfile)) {
        setUserProfile(mergedProfile);
      }

      if (profileNeedsServerSync(mergedProfile, cloudProfile)) {
        updateCloudProfile(toApiProfile(mergedProfile)).catch(console.error);
      }

      return;
    }

    if (userProfile) {
      syncProfile(toApiProfile(userProfile)).catch(console.error);
    }
  }, [
    cloudProfile,
    hasLoadedUserData,
    isAuthenticated,
    profilesAreEquivalent,
    syncProfile,
    updateCloudProfile,
    userProfile,
  ]);

  // Sync to cloud when profile changes (debounced)
  const handleSetUserProfile = useCallback(
    (value) => {
      setUserProfile(value);

      // If authenticated, sync to cloud
      if (
        isAuthenticated &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        // Convert local format to API format
        const apiProfile = toApiProfile(value);
        updateCloudProfile(apiProfile).catch(console.error);
      }
    },
    [isAuthenticated, updateCloudProfile],
  );

  return {
    userProfile,
    setUserProfile: handleSetUserProfile,
    llmSettings,
    setLlmSettings,
    isAuthenticated,
  };
};

export default useWorkoutProfile;
